// The wrapper every chart in this directory renders through, and the reason
// the contract is enforceable rather than remembered.
//
// TWO RULES LIVE HERE SO NO CHART CAN SKIP THEM.
//
// 1. `label` is required and becomes `role="img" aria-label`. The drawing is
//    not available to a screen reader, so the sentence has to carry it, and
//    `scripts/a11y_audit.py:64` scores an unlabelled inline <svg> as SERIOUS
//    under WCAG 1.1.1. Callers build a full sentence including the counts —
//    `Sparkline`'s call site is the model: "... over the last 30 days, 12 of
//    30 days measured".
//
// 2. `notMeasured` draws NOTHING and prints the backend's own sentence. Never
//    an empty axis, never a flat line, never a bar of height zero. This is the
//    same rule `SectionDropOff` and `ImpactRadius` each implement by hand, and
//    the italic + `data-measured="no"` treatment is screen-wide: that
//    attribute is both the CSS hook and what the tests assert on.

import type { ReactNode } from "react";
import "./charts.css";

export type FigureProps = {
  /** Read aloud in place of the drawing. Write a sentence, not a title. */
  label: string;
  /** Viewport width in user units. */
  width: number;
  height: number;
  /** When present, nothing is drawn and this sentence is shown instead. */
  notMeasured?: { reason: string };
  /** Origin shift, for charts that want a centred or inset coordinate space. */
  origin?: [number, number];
  children: ReactNode;
};

export function Figure({
  label,
  width,
  height,
  notMeasured,
  origin,
  children,
}: FigureProps) {
  if (notMeasured) {
    return (
      <p className="maisight-chart-absent type-bodyS" data-measured="no">
        {notMeasured.reason}
      </p>
    );
  }
  return (
    <svg
      className="maisight-chart"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={label}
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
    >
      {origin ? <g transform={`translate(${origin[0]},${origin[1]})`}>{children}</g> : children}
    </svg>
  );
}
